import { RequestOffersPanel } from "@/components/price/PriceOffers";
import React, { useState } from "react";
import { X, CalendarClock, CreditCard, Car, Loader2, Navigation } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { isNativeStripeAvailable } from "@/lib/stripeNative";
import ScheduleField, { toLocalInput } from "@/components/price/ScheduleField";

export const fmtSched = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const STATUS = {
  scheduled_open: { label: "Recherche d'un covoitureur", cls: "bg-[#FFF3CC]" },
  scheduled_awaiting_payment: { label: "Paiement à faire", cls: "bg-primary text-primary-foreground" },
  scheduled_accepted: { label: "Confirmée", cls: "bg-[#D6F5D6]" },
  accepted: { label: "En route", cls: "bg-[#D6F5D6]" },
  passenger_boarding: { label: "À bord", cls: "bg-[#D6F5D6]" },
  in_progress: { label: "En cours", cls: "bg-[#D6F5D6]" },
};

export async function openRidePayment(rideId, onDone) {
  const r = await api.post(`/price/ride-requests/${rideId}/checkout`, { origin_url: window.location.origin });
  const url = r.data?.checkout_url;
  if (!url) return;
  if (isNativeStripeAvailable()) {
    const { Browser } = await import("@capacitor/browser");
    const listener = await Browser.addListener("browserFinished", () => { listener.remove(); setTimeout(onDone, 1500); });
    await Browser.open({ url, presentationStyle: "popover" });
    return;
  }
  window.location.href = url;
}

export default function ScheduledRidesSheet({ open, onClose, rides, onReload, onFollow }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="scheduled-rides-sheet">
      <div className="w-full sm:max-w-md max-h-[88dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#FFF8F1] p-5 street-border street-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent street-border-thin"><CalendarClock className="h-5 w-5" strokeWidth={2.5} /></div>
            <div className="font-[Outfit] text-[18px] font-black">Mes courses programmées</div>
          </div>
          <button type="button" onClick={onClose} data-testid="scheduled-rides-close" className="grid h-9 w-9 place-items-center rounded-xl bg-white street-border-thin" aria-label="Fermer">
            <X className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>
        {rides.length === 0 && (
          <p className="mt-6 text-center text-[13px] font-semibold text-foreground/60" data-testid="scheduled-rides-empty">Aucune course programmée pour l&apos;instant.</p>
        )}
        <div className="mt-4 space-y-3">
          {rides.map((r) => <ScheduledRideCard key={r.id} r={r} onReload={onReload} onFollow={onFollow} />)}
        </div>
      </div>
    </div>
  );
}

function ScheduledRideCard({ r, onReload, onFollow }) {
  const [busy, setBusy] = useState(false);
  const st = STATUS[r.status] || { label: r.status, cls: "bg-white" };
  const live = ["accepted", "passenger_boarding", "in_progress"].includes(r.status);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const cancel = () => run(async () => {
    if (!window.confirm("Annuler cette course programmée ?")) return;
    const res = await api.post(`/price/ride-requests/${r.id}/cancel`);
    const refunded = res.data?.refunded_eur || 0;
    toast(refunded > 0 ? `Course annulée — ${refunded.toFixed(2)}€ remboursés` : "Course annulée");
    onReload();
  });
  const pay = () => run(() => openRidePayment(r.id, onReload));
  const [editing, setEditing] = useState(false);
  const [when, setWhen] = useState(() => toLocalInput(new Date(r.scheduled_at)));
  const canEdit = r.status === "scheduled_open" && !r.driver_id;
  const reschedule = () => run(async () => {
    const res = await api.post(`/price/ride-requests/${r.id}/reschedule`, { scheduled_at: new Date(when).toISOString() });
    toast.success(`Nouvel horaire enregistré — prix : ${Number(res.data.request.price_eur).toFixed(2)}€`);
    setEditing(false);
    onReload();
  });
  return (
    <div className="rounded-2xl bg-white p-4 street-border-thin street-shadow-sm" data-testid={`scheduled-ride-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-[Outfit] text-[15px] font-black capitalize">{fmtSched(r.scheduled_at)}</div>
        <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest street-border-thin ${st.cls}`} data-testid={`scheduled-ride-status-${r.id}`}>{st.label}</span>
      </div>
      <div className="mt-2 text-[12px] font-semibold text-foreground/70 leading-snug">{r.from_address} → {r.to_address}</div>
      <div className="mt-1 text-[12px] font-bold">{r.negotiable && r.status === "scheduled_open" ? "Recommandé " : ""}{Number(r.price_eur).toFixed(2)}€ · {r.payment_method === "card" ? "Carte" : "Espèces"} · {r.seats} place{r.seats > 1 ? "s" : ""}</div>
      {r.driver_name && (
        <div className="mt-2 flex items-center gap-2 rounded-xl bg-secondary/40 px-3 py-2 text-[12px] font-bold">
          <Car className="h-4 w-4 text-primary" /> {r.driver_name}{r.driver_car ? ` · ${r.driver_car}` : ""}
        </div>
      )}
      {r.negotiable && r.status === "scheduled_open" && !editing && (
        <div className="mt-3"><RequestOffersPanel requestId={r.id} recommended={r.recommended_price_eur} onAgreed={onReload} /></div>
      )}
      {editing && (
        <div className="mt-3 space-y-2 rounded-xl bg-[#FFF8F1] p-3 street-border-thin" data-testid={`scheduled-ride-edit-${r.id}`}>
          <ScheduleField value={when} onChange={setWhen} testid={`scheduled-ride-edit-input-${r.id}`} />
          <p className="text-[11px] font-semibold text-foreground/60">Le prix est recalculé selon le nouveau délai de réservation.</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setEditing(false)} className="flex-1 rounded-xl bg-white py-2 text-[13px] font-black street-border-thin" data-testid={`scheduled-ride-edit-cancel-${r.id}`}>Retour</button>
            <button type="button" disabled={busy} onClick={reschedule} className="street-press flex-1 rounded-xl bg-primary py-2 text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-60" data-testid={`scheduled-ride-edit-save-${r.id}`}>Enregistrer</button>
          </div>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        {canEdit && !editing && (
          <button type="button" onClick={() => setEditing(true)} data-testid={`scheduled-ride-reschedule-${r.id}`} className="street-press flex-1 rounded-xl bg-white py-2.5 text-[13px] font-black street-border-thin">
            Modifier l&apos;heure
          </button>
        )}
        {r.status === "scheduled_awaiting_payment" && (
          <button type="button" disabled={busy} onClick={pay} data-testid={`scheduled-ride-pay-${r.id}`} className="street-press flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary py-2.5 text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Payer {Number(r.price_eur).toFixed(2)}€
          </button>
        )}
        {live && (
          <button type="button" onClick={() => onFollow(r)} data-testid={`scheduled-ride-follow-${r.id}`} className="street-press flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary py-2.5 text-[13px] font-black text-primary-foreground street-border-thin">
            <Navigation className="h-4 w-4" /> Suivre ma course
          </button>
        )}
        {!live && !editing && (
          <button type="button" disabled={busy} onClick={cancel} data-testid={`scheduled-ride-cancel-${r.id}`} className="street-press flex-1 rounded-xl bg-white py-2.5 text-[13px] font-black street-border-thin disabled:opacity-60">
            Annuler la course
          </button>
        )}
      </div>
    </div>
  );
}
